import * as echarts from 'echarts/core';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import { ECOption } from '../../model';
import './pie.scss';
import { DEFAULT_COLOR_LIST } from './pie-color';
import getFormatMessage from '../../locales/get-format-message';

interface IProps {
  title?: string;
  subTitle?: string;
  className?: string;
  style?: CSSProperties;
  menuName?: string;
  unit?: string;
  isProduct?: boolean;
  styleType?: number;
  seriesData: ReadonlyArray<
    Array<{ name: string; value: number; services?: string[]; color?: string }>
  >;
}

export const BillChartPie = (props: IProps) => {
  const chartContainer = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<any>(null);
  const rightSideContainer = useRef<HTMLDivElement>(null); // 右侧列表容器
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null); // 高亮的索引
  const { style, title, subTitle, seriesData, isProduct } = props;

  useEffect(() => {
    if (!chartContainer.current) {
      return;
    }

    let colorList: string[] = [];
    if (isProduct) {
      seriesData[0].forEach((item) => {
        colorList.push(item.color || '');
      });
    } else {
      colorList = DEFAULT_COLOR_LIST;
    }

    chartInstance.current = echarts.init(chartContainer.current);

    const option: ECOption = {
      title: {
        text: title,
        subtext: subTitle,
        left: 'center',
        top: '40%',
        textStyle: {
          fontSize: 30,
          lineHeight: 30,
          color: '#1D2129', // 主标题颜色
        },
        subtextStyle: {
          fontSize: 12,
          lineHeight: 12,
          color: '#86909C', // 副标题颜色
        },
      },
      tooltip: {
        trigger: 'item',
        formatter: '{b} <br/> ¥{c} ({d}%)',
      },
      series: seriesData.map((series) => {
        return {
          name: title,
          type: 'pie',
          radius: ['50%', '60%'],
          data: series,
          label: {
            show: false,
            position: 'center',
          },
          emphasis: {
            label: {
              show: true,
              fontSize: '20',
              fontWeight: 'bold',
              color: '#fff',
            },
          },
          color: colorList,
        };
      }),
    };

    chartInstance.current.setOption(option);

    // 监听环形图的 'mouseover' 事件，获取鼠标所在的扇区
    chartInstance.current.on('mouseover', (params: any) => {
      const { dataIndex, seriesIndex } = params;

      // 从 seriesData 中获取对应的项
      const name = seriesData[seriesIndex]?.[dataIndex]?.name;

      if (name) {
        const index = seriesData[seriesIndex].findIndex(
          (item) => item.name === name
        );
        setHighlightedIndex(index);

        // 如果右侧列表存在并且条目不在视图中，则滚动到该条目
        if (rightSideContainer.current && index !== -1) {
          const container = rightSideContainer.current.querySelector(
            '.pie-menu-box'
          ) as HTMLElement | null;
          const itemElement = container?.querySelector(
            `#item-${index}`
          ) as HTMLElement | null;
          // const itemElement = rightSideContainer.current.querySelector(
          //   `#item-${index}`
          // );
          if (container && itemElement) {
            // itemElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
            // 计算滚动位置，让目标元素可见
            const containerRect = container.getBoundingClientRect(); // 容器的位置信息
            const itemRect = itemElement.getBoundingClientRect(); // 目标元素的位置信息

            const itemOffsetTop =
              itemRect.top - containerRect.top + container.scrollTop;
            const containerHeight = container.clientHeight;
            const itemHeight = itemElement.clientHeight;

            if (itemOffsetTop < container.scrollTop) {
              // 在上方 -> 滚动到目标项的顶部
              container.scrollTo({ top: itemOffsetTop, behavior: 'smooth' });
            } else if (
              itemOffsetTop + itemHeight >
              container.scrollTop + containerHeight
            ) {
              // 在下方 -> 滚动到底部
              container.scrollTo({
                top: itemOffsetTop - containerHeight + itemHeight,
                behavior: 'smooth',
              });
            }
          }
        }
      }
    });

    // 监听环形图的 'mouseout' 事件
    chartInstance.current.on('mouseout', () => {
      setHighlightedIndex(null); // 鼠标移出时取消高亮
    });

    // 监听右侧列表的高亮事件
    const handleRightSideHover = (index: number) => {
      setHighlightedIndex(index);
      chartInstance.current.dispatchAction({
        type: 'highlight',
        seriesIndex: 0,
        dataIndex: index,
      });
    };

    // 右侧列表的鼠标悬停
    const rightSideItems =
      rightSideContainer.current?.querySelectorAll('.right-side-item');
    rightSideItems?.forEach((item, index) => {
      item.addEventListener('mouseenter', () => handleRightSideHover(index));
      item.addEventListener('mouseleave', () => {
        setHighlightedIndex(null);
        chartInstance.current.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
          dataIndex: index,
        });
      });
    });

    // 监听窗口大小变化
    const handleResize = () => {
      chartInstance.current?.resize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current.dispose();
    };
  }, [seriesData, title, subTitle]);

  return (
    <div style={style} className={props.className}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* 左侧为饼图 */}
        <div
          style={{
            width: props.styleType === 1 ? '35%' : '55%',
            height: '260px',
            marginRight: '20px',
          }}
          ref={chartContainer}
        ></div>

        {/* 右侧为产品信息 */}
        {!props.styleType && seriesData[0]?.length !== 0 && (
          <div
            ref={rightSideContainer}
            style={{
              width: '80%',
              padding: '20px',
            }}
          >
            <div
              style={{
                marginBottom: '4px',
                display: 'flex',
                height: '18px',
                color: '#C9CDD4',
                fontSize: '12px',
                paddingRight: '37px',
                paddingLeft: '12px',
              }}
            >
              <div style={{ width: '150px' }}>
                {props.menuName
                  ? props.menuName
                  : getFormatMessage('name_uppercase')}
              </div>
              <div style={{ width: '90px' }}>
                {getFormatMessage('bill_management_amount')}
              </div>
              <div style={{ flex: 1 }}>
                {getFormatMessage('bill_management_proportion')}
              </div>
            </div>
            <div
              style={{
                overflow: 'hidden auto',
                height: '190px',
                paddingRight: '20px',
              }}
              className="pie-menu-box"
            >
              {seriesData[0].map((item, index) => (
                <div
                  id={`item-${index}`} // 每个条目有一个唯一ID
                  key={item.name}
                  className="right-side-item"
                  style={{
                    // display: 'flex',
                    // justifyContent: 'space-between',
                    height: '48px',
                    lineHeight: '48px',
                    backgroundColor:
                      highlightedIndex === index ? '#F7F8FA' : '#fff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    padding: '0 12px',
                    marginBottom: '2px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      borderBottom: '1px solid #E5E6EB',
                    }}
                  >
                    <div style={{ display: 'flex', marginRight: '8px' }}>
                      <div
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '2px',
                          marginRight: '4px',
                          marginTop: '20px',
                          background: item.color || DEFAULT_COLOR_LIST[index],
                        }}
                      ></div>
                      <div
                        style={{
                          width: '130px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.name}
                      </div>
                    </div>
                    <div style={{ width: '90px', fontWeight: 600 }}>{`${
                      props?.unit || '¥'
                    } ${item.value.toFixed(2)}`}</div>
                    <div style={{ flex: 1 }}>
                      {(
                        (item.value /
                          seriesData[0].reduce(
                            (acc, curr) => acc + curr.value,
                            0
                          )) *
                        100
                      ).toFixed(2)}
                      %
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {props.styleType === 1 && (
          <div
            ref={rightSideContainer}
            style={{
              width: '150%',
              padding: '20px',
              display: 'flex',
              gap: '50px',
            }}
          >
            {seriesData[0].map((item, index) => (
              <div
                id={`item-${index}`} // 每个条目有一个唯一ID
                key={item.name}
                className="right-side-item"
                style={{
                  width: '28%',
                  minHeight: '190px',
                  backgroundColor:
                    highlightedIndex === index ? '#F7F8FA' : '#fff',
                  cursor: 'pointer',
                  fontSize: '14px',
                  padding: '12px',
                  borderRadius: '4px',
                }}
              >
                <div
                  style={{
                    fontWeight: 400,
                    lineHeight: '22px',
                    display: 'flex',
                  }}
                >
                  <div
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '2px',
                      marginRight: '4px',
                      marginTop: '7px',
                      background: DEFAULT_COLOR_LIST[index],
                    }}
                  ></div>
                  <div>
                    {item.name === 'mode_one'
                      ? getFormatMessage('bill_management_usage')
                      : ''}
                  </div>
                </div>
                <div
                  style={{
                    borderBottom: '1px solid #E5E6EB',
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px 0 12px',
                    marginBottom: '12px',
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: '16px' }}>{`${
                    props?.unit || '¥'
                  } ${item.value.toFixed(2)}`}</span>
                  <span>
                    {(
                      (item.value /
                        seriesData[0].reduce(
                          (acc, curr) => acc + curr.value,
                          0
                        )) *
                      100
                    ).toFixed(2)}
                    %
                  </span>
                </div>
                <div>
                  {item.services?.map((itemName) => {
                    return (
                      <div
                        style={{
                          color: '#86909c',
                          fontSize: '12px',
                          fontWeight: 400,
                          lineHeight: '22px',
                          marginBottom: '4px',
                        }}
                      >
                        {itemName}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
